import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import Card from 'components/card';
import Button from 'components/button';
import SimpleTable from 'components/simpleTable';
import Header from 'components/text/header';
import UseApi from 'hooks/useApi';
import { HiArrowLeft } from 'react-icons/hi';
import Loader from 'components/loader';

const queryClient = new QueryClient();

const BookingData = () => {
  const { id } = useParams();
  const { getApi, postApi } = UseApi();
  const navigate = useNavigate();

  async function fetchData(id: string) {
    const response = await getApi(`bookings/${id}`);
    return response.data;
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['data', id],
    queryFn: () => fetchData(id),
  });

  if (isLoading) return <Loader />;
  if (error) return <Header>An error has occurred: {error.message}</Header>;
  
  const { 
    persons = [], 
    billing = null, 
    package: tripPackage = null, 
    user = null, 
    state = '' 
  } = data || {};

  const approve = async () => {
    try {
      await postApi(`bookings/${id}/accept`, {});
      navigate('/admin/booking');
    } catch (error) {
      console.error('Error approving booking:', error);
    }
  };
  const reject = async () => {
    try {
      await postApi(`bookings/${id}/reject`, {});
      navigate('/admin/booking');
    } catch (error) {
      console.error('Error rejecting booking:', error);
    }
  };
  
  return (
    <div className="flex flex-col gap-3">
      <Link key={1} to={'/admin/booking'}>
        <Button variant="gray" extraClasses="flex items-center gap-2 ">
          <HiArrowLeft />
          Back
        </Button>
      </Link>

      <Card extra="gap-2 p-4">
        <div className="ml-8 flex flex-col gap-4">
          <div>
            <Header extraClasses="inline ">Package </Header>
            <Link target="_blank" to={`/admin/package/${tripPackage?._id}`}>
              <p className="text-lg font-medium text-navy-700 hover:text-navy-600 hover:underline">
                {tripPackage?.packageName || 'No package selected'}
              </p>
            </Link>
          </div>
          <div>
            <Header extraClasses="inline ">User </Header>
            <Link target="_blank" to={`/admin/user/${user?._id}`}>
              <p className="text-lg font-medium text-navy-700 hover:text-navy-600 hover:underline">
                {user?.username || user?.email || 'No user information'}
              </p>
            </Link>
          </div>
        </div>

        {/* Conditionally render BillingData only if billing exists */}
        {billing ? (
          <BillingData billing={billing} />
        ) : (
          <div className="m4 p-4">
            <Header>Billing Details</Header>
            <p className="text-gray-500">No billing information available</p>
          </div>
        )}
        
        {/* Conditionally render PersonsData only if persons array has items */}
        {persons && persons.length > 0 ? (
          <PersonsData persons={persons} />
        ) : (
          <div className="m4 p-4">
            <Header>Persons Details</Header>
            <p className="text-gray-500">No person details available</p>
          </div>
        )}
        
        {state === 'pending' && (
          <>
            <Button extraClasses="m-auto" variant="success" onClick={approve}>
              Approve
            </Button>
            <Button extraClasses="m-auto" variant="error" onClick={reject}>
              Reject
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

const PersonsData = ({ persons }: any) => {
  return (
    <div className="m4  p-4">
      <Header>Persons Details</Header>
      {persons?.map((person: any, index: number) => (
        <SimpleTable
          key={index}
          data={[
            { key: 'First Name', value: person?.firstName || 'N/A' },
            { key: 'Last Name', value: person?.lastName || 'N/A' },
            { key: 'Room Type', value: person?.roomType || 'N/A' },
          ]}
          extraClasses="pb-2  border-b border-gray-100 "
        />
      ))}
    </div>
  );
};

const BillingData = ({ billing }: any) => {
  const {
    bookingAmount = 'N/A',
    VAT = 'N/A',
    discount = 'N/A',
    totalAmount = 'N/A',
    firstName = 'N/A',
    lastName = 'N/A',
    email = 'N/A',
    phone = 'N/A',
    address = 'N/A',
    city = 'N/A',
    zip = 'N/A',
    comments = 'N/A',
    paymentMethod = 'N/A',
    coupon = 'N/A',
    receipt = null,
  } = billing || {};

  return (
    <div className="m4  p-4">
      <SimpleTable
        header="Billing Details"
        data={[
          { key: 'BookingAmount', value: bookingAmount },
          { key: 'VAT', value: VAT },
          { key: 'Discount', value: discount },
          { key: 'Total Amount', value: totalAmount },
          { key: 'First Name', value: firstName },
          { key: 'Last Name', value: lastName },
          { key: 'Email', value: email },
          { key: 'Phone', value: phone },
          { key: 'Address', value: address },
          { key: 'City', value: city },
          { key: 'Zip', value: zip },
          { key: 'Comments', value: comments },
          { key: 'PaymentMethod', value: paymentMethod },
          { key: 'Coupon', value: coupon },
          {
            key: 'Receipt',
            value: receipt ? (
              <a href={receipt} target="#">
                <img src={receipt} width={120} height={60} alt="Receipt" />
              </a>
            ) : 'No receipt available',
          },
        ]}
      />
    </div>
  );
};

const BookingDetails = (props: {}) => {
  return (
    <QueryClientProvider client={queryClient}>
      <BookingData />
    </QueryClientProvider>
  );
};

export default BookingDetails;